import type { TFunction } from 'i18next';
import type { SubmitEventHandler } from 'react';
import type { DropdownFilterCallback } from '../../components/Dropdown/Dropdown';
import type { DropdownOption } from '../../components/Dropdown/DropdownOption/DropdownOption';
import type { ApiKey } from '../../lib/flotiq-client/types/api-response';

import { Fragment, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import copy from 'copy-to-clipboard';
import moment from 'moment/moment';
import { twMerge } from 'tailwind-merge';
import z from 'zod';

import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import SaveButton from '../../components/Button/predefined/SaveButton/SaveButton';
import Loader from '../../components/Loader/Loader';
import PopoverComponent from '../../components/Popover/Popover';
import PopoverItem from '../../components/Popover/PopoverItem/PopoverItem';
import Tooltip from '../../components/Tooltip/Tooltip';

import {
  BookIcon,
  CopySolicIcon,
  DeleteIcon,
  DotsIcon,
  HistoryIcon,
  QrCodeIcon,
  ReloadIcon,
  TrashSolidIcon,
} from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

export type ApiKeyFormSubmitFn = ({
  value,
}: {
  value: Pick<ApiKey, 'id' | 'apiKey' | 'name'> & {
    permissions: {
      canRead: boolean;
      canUpdate: boolean;
      canCreate: boolean;
      canDelete: boolean;
      contentTypeDefinition?: { id: string };
      type?: string;
    }[];
  };
}) => void;

export type GenerateApiKeySchemaFn = (
  name: string,
  apiKey: string,
  generateSchema?: boolean,
) => void;

export type RegenerateApiKeyFn = (name: string, id: string) => void;

export type DeleteApiKeyFn = (
  id: string,
  name: string,
  type?: 'unsaved',
) => void;

export type ShowApiKeyQrCodeFn = (name: string, apiKey: string) => void;
export type ShowApiKeyHistoryFn = (
  id: string,
  name: string,
  apiKey: string,
) => void;

interface ApiKeyFormProps {
  /**
   *  Current options like name, api key, permissions
   */
  data?: ApiKey;
  /**
   * If current api key could be removed
   */
  removable?: boolean;
  /**
   * If input name could be edited
   */
  editableName?: boolean;
  /**
   *  Show section with rules
   */
  rules?: boolean;
  /**
   * Options
   */
  options?: DropdownOption[];
  /**
   * Options filter
   */
  optionsFilterCallback?: DropdownFilterCallback;
  /**
   * Callback to get schema
   */
  onGetSchema?: GenerateApiKeySchemaFn;
  /**
   * Callback to regenerate api key
   */
  onRegenerate?: RegenerateApiKeyFn;
  /**
   * Callback to delete api key
   */
  onDelete?: DeleteApiKeyFn;
  /**
   * Callback to show QR code
   */
  onShowQRCode?: ShowApiKeyQrCodeFn;
  /**
   * Callback to show API key history
   */
  onShowHistory?: ShowApiKeyHistoryFn;
  /**
   * Callback to save api key
   */
  onSave?: ApiKeyFormSubmitFn;
  /**
   * If form is loading
   */
  isLoading?: boolean;
  /**
   * Test id for layout
   */
  testId?: string;
  /**
   * Allow to generate schema
   */
  generateSchema?: boolean;
}

const getInitialValues = (t: TFunction, data?: ApiKey) => ({
  id: data?.id || '_newApiKey',
  apiKey: data?.apiKey || '',
  name: data?.name || t('ApiKeys.NewKey'),
  permissions: data?.permissions?.map((permission) => ({
    canRead: permission.canRead,
    canUpdate: permission.canUpdate,
    canCreate: permission.canCreate,
    canDelete: permission.canDelete,
    contentTypeDefinition: { id: permission.contentTypeDefinition?.id },
    type: permission.type,
  })) || [
    {
      canRead: false,
      canUpdate: false,
      canCreate: false,
      canDelete: false,
      contentTypeDefinition: { id: '' },
      type: '',
    },
  ],
});

const ICON_BUTTON_CLASSES =
  'w-8 h-8 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center rounded-lg';

const ApiKeyForm = ({
  data,
  options = EMPTY_ARRAY,
  optionsFilterCallback,
  removable = false,
  editableName = false,
  rules = true,
  generateSchema = false,
  onGetSchema,
  onRegenerate,
  onDelete,
  onShowQRCode,
  onShowHistory,
  onSave,
  isLoading = false,
  testId = '',
}: ApiKeyFormProps) => {
  const { t } = useTranslation();
  const apiKey = data?.apiKey || '';
  const id = data?.id || '_newApiKey';

  const validationSchema = useMemo(
    () =>
      z.object({
        id: z.string(),
        apiKey: z.string(),
        name: z.string().min(1, t('Form.FormErrorNotBlank')),
        permissions: z.array(
          z.object({
            contentTypeDefinition: z.object({
              id: z.string().min(1, t('Form.FormErrorNotBlank')),
            }),
            canRead: z.boolean(),
            canCreate: z.boolean(),
            canUpdate: z.boolean(),
            canDelete: z.boolean(),
            type: z.string(),
          }),
        ),
      }),
    [t],
  );

  const form = useAppForm({
    defaultValues: getInitialValues(t, data),
    onSubmit: onSave,
    validators: {
      onChange: validationSchema,
    },
  });

  const onPermissionAdd = useCallback(() => {
    form.pushFieldValue('permissions', {
      canCreate: false,
      canRead: false,
      canUpdate: false,
      canDelete: false,
      contentTypeDefinition: { id: '' },
      type: '',
    });
  }, [form]);

  const baseApiKeyActions = useMemo(() => {
    const currentName = form.getFieldValue('name');
    return [
      {
        id: `${id}-copy`,
        icon: (
          <CopySolicIcon className="w-3.5 h-4 text-slate-700 dark:text-slate-100" />
        ),
        label: t('ApiKeys.CopyToClipboard'),
        onClick: async () => {
          const copied = await copy(apiKey);
          if (copied) {
            toast.success(t('ApiKeys.CopiedApiKey'));
          }
        },
      },
      {
        id: `${id}-schema`,
        icon: (
          <BookIcon
            className={twMerge(
              'h-3.5 w-4 text-slate-700 dark:text-slate-100',
              !generateSchema && 'opacity-50',
            )}
          />
        ),
        onClick: () => onGetSchema?.(currentName, apiKey, generateSchema),
        tooltip: generateSchema
          ? t('ApiKeys.GetAPIDoc')
          : t('ApiKeys.LimitsSchema'),
        label: t('ApiKeys.GetSchema'),
      },
    ];
  }, [apiKey, form, generateSchema, id, onGetSchema, t]);

  const moreApiKeyActions = useMemo(() => {
    const currentName = form.getFieldValue('name');
    return [
      {
        id: `${id}-history`,
        icon: (
          <HistoryIcon className="shrink-0 h-3 w-3.5 text-slate-700 dark:text-slate-300" />
        ),
        onClick: () => onShowHistory?.(id, currentName, apiKey),
        label: t('ApiKeys.History.Title'),
      },
      {
        id: `${id}-regenerate`,
        icon: (
          <ReloadIcon className="h-4 w-4 text-slate-700 dark:text-slate-300" />
        ),
        onClick: () => onRegenerate?.(currentName, id),
        label: t('ApiKeys.RegenerateConfirm.Regenerate'),
      },
      {
        id: `${id}-qr`,
        icon: (
          <QrCodeIcon className="h-3 w-3 text-slate-700 dark:text-slate-300" />
        ),
        onClick: () => onShowQRCode?.(currentName, apiKey),
        label: t('ApiKeys.ShowQRCode'),
      },
      ...(removable
        ? [
            {
              id: `${id}-remove`,
              icon: <TrashSolidIcon className="h-3 w-3" />,
              onClick: () => onDelete?.(id, currentName),
              label: t('Global.Delete'),
              color: 'red' as const,
            },
          ]
        : []),
    ];
  }, [
    form,
    id,
    t,
    removable,
    onShowHistory,
    onRegenerate,
    onShowQRCode,
    apiKey,
    onDelete,
  ]);

  const ruleColumn = useMemo(() => {
    return ['Read', 'Create', 'Update', 'Delete'].map((perm) => ({
      id: `${id}-can${perm}`,
      name: `can${perm}`,
      label: t(`Global.Permission.${perm}`),
    }));
  }, [id, t]);

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        className={twMerge(
          'rounded-lg bg-white dark:bg-slate-950 relative',
          'my-2 border dark:border-0 flex-col items-center',
        )}
        onSubmit={handleSubmit}
        noValidate={true}
        {...getTestProps(testId, `${id}-container`)}
      >
        {isLoading && (
          <div
            className={twMerge(
              'h-full w-full z-10 absolute top-0 left-0',
              'overflow-hidden flex justify-center items-center',
            )}
            {...getTestProps(testId, 'loader')}
          >
            <Loader type="spinner-grid" size="big" />
          </div>
        )}

        <div
          className={twMerge(
            isLoading && 'opacity-50 selection-none pointer-events-none',
          )}
        >
          <div className="flex flex-col lg:flex-row w-full items-center p-5">
            {/* Section: Name */}
            <form.AppField name="name">
              {(field) => (
                <field.Input
                  additionalClasses={twMerge(
                    'mr-2 mb-2 lg:mb-0',
                    !editableName && 'cursor-not-allowed',
                  )}
                  additionalInputClasses={twMerge(
                    !editableName && 'pointer-events-none',
                  )}
                  {...getTestProps(testId, `${id}-name`, 'testId')}
                />
              )}
            </form.AppField>

            {/* Section: Api Key */}
            <form.AppField name="apiKey">
              {(field) => (
                <field.Input
                  additionalClasses={'mr-2 sm:min-w-[250px] mb-4 lg:mb-0'}
                  readOnly
                  disabled={!apiKey}
                  notSensitiveToPasswordManager={!!apiKey}
                  placeholder={t('ApiKeys.SaveApiToGenerateToken')}
                  additionalInputClasses={
                    'text-sm sm:text-base placeholder:text-slate-900 ' +
                    'placeholder:font-normal dark:placeholder:text-white'
                  }
                  {...getTestProps(testId, `${id}-api-key`, 'testId')}
                />
              )}
            </form.AppField>

            <form.Subscribe
              selector={({ values }) => ({
                apiKey: values.apiKey,
                name: values.name,
              })}
            >
              {({ apiKey, name }) =>
                apiKey ? (
                  <div
                    className={
                      'flex flex-wrap lg:flex-nowrap lg:flex-col gap-3 lg:gap-0.5 w-full lg:w-fit ' +
                      'justify-between items-center lg:justify-center lg:items-end'
                    }
                  >
                    <div className="ml-auto lg:ml-0 flex flex-wrap lg:flex-nowrap gap-1.5 lg:gap-3 items-center">
                      {/* Section: Api Options */}
                      {baseApiKeyActions.map((item) => (
                        <Fragment key={item.id}>
                          <Tooltip
                            tooltip={item.tooltip || item.label || ''}
                            tooltipPlacement="topCenter"
                            additionalTooltipClasses="hidden lg:flex"
                            additionalClasses="hidden lg:flex flex-col items-center justify-center "
                          >
                            <span className="lg:hidden">{item.label}</span>
                            <button
                              className={ICON_BUTTON_CLASSES}
                              type="button"
                              onClick={item.onClick}
                              {...getTestProps(testId, `${item.id}-option`)}
                            >
                              {item.icon}
                            </button>
                          </Tooltip>

                          <Button
                            iconImage={item.icon}
                            iconPosition="start"
                            type="button"
                            onClick={item.onClick}
                            buttonColor="grayBordered"
                            additionalClasses="lg:hidden"
                          >
                            {item.label}
                          </Button>
                        </Fragment>
                      ))}

                      <PopoverComponent
                        content={moreApiKeyActions.map((action) => (
                          <PopoverItem
                            key={action.id}
                            iconPosition="start"
                            label={action.label}
                            iconImage={action.icon}
                            color={action.color}
                            onClick={action.onClick}
                            {...getTestProps(testId, `${action.id}-option`)}
                          />
                        ))}
                        popoverButton={
                          <div
                            className={ICON_BUTTON_CLASSES}
                            {...getTestProps(testId, `${id}-open-more-options`)}
                          >
                            <DotsIcon className="w-1 h-4 dark:text-slate-100" />
                          </div>
                        }
                        popoverPanelAnchorTo="bottom end"
                        additionalPopoverPanelClasses="-mt-2"
                      />
                    </div>
                    <div
                      className="order-first lg:order-none px-2 text-slate-600 dark:text-slate-200 
                                lg:text-end whitespace-nowrap"
                    >
                      {!data?.lastUsedAt ? (
                        t('ApiKeys.NeverUsed')
                      ) : (
                        <Trans
                          i18nKey="ApiKeys.LastUsed"
                          values={{
                            date: moment(data.lastUsedAt).format('DD-MM-YYYY'),
                          }}
                        ></Trans>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-row flex-wrap lg:flex-nowrap justify-around">
                    <Button
                      onClick={() => onDelete?.(id, name, 'unsaved')}
                      iconImage={
                        <DeleteIcon className={'w-5 h-5 dark:text-slate-100'} />
                      }
                      buttonColor="borderless"
                      iconPosition="top"
                      buttonSize="lg"
                      additionalChildrenClasses={twMerge(
                        'text-sm whitespace-nowrap',
                        'flex lg:hidden',
                      )}
                      {...getTestProps(testId, `${id}-remove-unsaved`)}
                    >
                      {t('Global.Delete')}
                    </Button>
                  </div>
                )
              }
            </form.Subscribe>
          </div>

          {rules && (
            <>
              <form.AppField name={'permissions'} mode="array">
                {(field) => (
                  <div
                    className={twMerge(
                      'lg:border-t dark:border-0 px-5',
                      field.state.value?.length > 0 &&
                        'pt-3 pb-3 border-b dark:border-slate-800',
                    )}
                  >
                    <field.PermissionsField
                      ruleColumn={ruleColumn}
                      ctdsOptions={options}
                      ctdsFilterCallback={optionsFilterCallback}
                      ctdPlaceholder={t('Global.SelectContentType')}
                      hasAddButton={false}
                      {...getTestProps(testId, id, 'testId')}
                    />
                  </div>
                )}
              </form.AppField>

              <div className="flex flex-row w-full p-5">
                <Button
                  onClick={onPermissionAdd}
                  {...getTestProps(testId, `${id}-button-add-rule`)}
                >
                  {t('ApiKeys.AddRule')}
                </Button>

                <SaveButton
                  type="submit"
                  additionalClasses={'ml-auto'}
                  label={t('Global.Save')}
                  {...getTestProps(testId, `${id}-button-save`)}
                />
              </div>
            </>
          )}
        </div>
      </form>
    </form.AppForm>
  );
};

export default ApiKeyForm;
